/**
 * 녹음 바 컴포넌트
 * 
 * 기능:
 * - 웨이브폼 시각화
 * - 시간 표시 (HH:MM:SS)
 * - 재생, 녹음/일시중지, 정지 버튼
 * - 녹음 완료 후: 파일명 입력 + 삭제/저장 버튼
 * - 최대 3시간 녹음 지원
 */
import React, { useMemo } from 'react';
import {
  RecordingModalOverlay,
  RecordingModal,
  RecordingTimer,
  RecordingButtonGroup,
  WaveformContainer,
  WaveformBar,
  IconButton,
  CloseButton,
  RecordingFooter,
  FileNameInput,
  FooterButtonGroup,
  DeleteButton,
  SaveButton,
} from '../styles';

interface RecordingModalComponentProps {
  isOpen: boolean;
  /** 마이크 에러 모달 (연결 안됨, 권한 거부 등) - 녹음 모달 위에 표시 */
  micErrorModal?: { isOpen: boolean; message: string } | null;
  onCloseMicError?: () => void;
  /** 마이크 권한 요청 모달 (녹음 전 안내) */
  isPermissionRequestModalOpen?: boolean;
  onOpenPermissionRequestModal?: () => void;
  onClosePermissionRequestModal?: () => void;
  onConfirmPermissionAndStart?: () => boolean | Promise<boolean>;
  isRecording: boolean;
  isPaused?: boolean;
  recordingTime: number;
  isUploading: boolean;
  isPlaying?: boolean;
  audioChunksLength: number;
  fileName: string;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  baseFileName: string;
  onStartRecording?: () => void;
  onStopRecording: () => void;
  onPauseRecording?: () => void;
  onResumeRecording?: () => void;
  onCancelRecording: () => void;
  onCloseModal?: () => void; // 모달만 닫기 (상태 유지)
  onUploadRecording: () => void;
  onResetRecording: () => void;
  onPlayPreview?: () => void;
  onStopPreview?: () => void;
  formatRecordingTime: (seconds: number) => string;
  progressPercent?: number;
  showTitleInput?: boolean;
  closeOnOutsideClick?: boolean; // 외부 클릭 시 닫기 옵션
}

const RecordingModalComponent: React.FC<RecordingModalComponentProps> = ({
  isOpen,
  micErrorModal,
  onCloseMicError,
  isPermissionRequestModalOpen,
  onOpenPermissionRequestModal,
  onClosePermissionRequestModal,
  onConfirmPermissionAndStart,
  isRecording,
  isPaused = false,
  recordingTime,
  isUploading,
  isPlaying = false,
  audioChunksLength,
  fileName,
  setFileName,
  baseFileName,
  onStartRecording,
  onStopRecording,
  onPauseRecording,
  onResumeRecording,
  onCancelRecording,
  onCloseModal,
  onUploadRecording,
  onResetRecording,
  onPlayPreview,
  onStopPreview,
  formatRecordingTime,
  showTitleInput = true,
  closeOnOutsideClick = true,
}) => {
  // 녹음 시작 알림 상태 (Hook은 항상 최상단에서 호출)
  const [showStartNotification, setShowStartNotification] = React.useState(false);
  const notificationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // 웨이브폼 바 생성 (20개)
  const waveformBars = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: i * 50,
    }));
  }, []);

  // 알림 모달 닫기 핸들러
  const handleCloseNotification = React.useCallback(() => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
    setShowStartNotification(false);
  }, []);

  // 컴포넌트 언마운트 시 타이머 정리
  React.useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  // micErrorModal이 있으면 모달이 닫혀있어도 에러 모달만 표시 (인라인 녹음 등 모달 없이 녹음 시도 시)
  if (!isOpen && !micErrorModal?.isOpen) return null;

  // 녹음 중 또는 일시중지 상태인지
  const isActiveRecording = isRecording || isPaused;
  // 녹음 완료 후 상태인지 (정지 버튼 누른 후)
  const hasRecordedAudio = !isRecording && !isPaused && audioChunksLength > 0;
  // 재생 가능한지 (녹음된 데이터가 있는지)
  const canPlay = audioChunksLength > 0;

  // 저장 핸들러
  const handleSave = () => {
    // 업로드 실행
    onUploadRecording();
  };

  // 삭제 핸들러
  const handleDelete = () => {
    onResetRecording();
    onCancelRecording();
  };

  // 모달 외부 클릭 핸들러
  const handleOverlayClick = () => {
    if (!closeOnOutsideClick) return;

    // 마이크 에러 모달만 표시 중이면 에러 모달 닫기
    if (!isOpen && micErrorModal?.isOpen) {
      onCloseMicError?.();
      return;
    }

    // 녹음 중이어도 모달만 닫기 (녹음은 백그라운드에서 계속 진행)
    if (onCloseModal) {
      onCloseModal();
    } else {
      // onCloseModal이 없으면 녹음 중이 아니면만 닫기
      if (!isActiveRecording) {
        onCancelRecording();
      }
    }
  };

  // 닫기 버튼 핸들러 (녹음 중이어도 모달만 닫고 녹음은 계속 진행)
  const handleCloseButton = () => {
    // 녹음 중이어도 모달만 닫기 (녹음은 백그라운드에서 계속 진행)
    if (onCloseModal) {
      onCloseModal();
    } else {
      // onCloseModal이 없으면 녹음 중이 아니면만 닫기
      if (!isActiveRecording) {
        if (hasRecordedAudio) {
          onCancelRecording();
        } else {
          onCancelRecording();
        }
      }
    }
  };

  // 녹음 시작 핸들러: 바로 권한 확인 후 녹음 시작 (권한 안내 모달 생략)
  const handleStartRecording = async () => {
    if (onConfirmPermissionAndStart) {
      const success = await onConfirmPermissionAndStart();
      if (success) {
        setShowStartNotification(true);
        notificationTimeoutRef.current = setTimeout(() => {
          setShowStartNotification(false);
          notificationTimeoutRef.current = null;
        }, 5000);
      }
    } else if (onStartRecording) {
      onStartRecording();
      setShowStartNotification(true);
      notificationTimeoutRef.current = setTimeout(() => {
        setShowStartNotification(false);
        notificationTimeoutRef.current = null;
      }, 5000);
    }
  };

  // 권한 모달에서 확인 시: 녹음 시작 + 알림 (성공한 경우에만)
  const handleConfirmPermissionAndStart = async () => {
    if (onConfirmPermissionAndStart) {
      const success = await onConfirmPermissionAndStart();
      if (success) {
        setShowStartNotification(true);
        notificationTimeoutRef.current = setTimeout(() => {
          setShowStartNotification(false);
          notificationTimeoutRef.current = null;
        }, 5000);
      }
    }
  };

  return (
    <RecordingModalOverlay onClick={handleOverlayClick} data-recording-modal="overlay">
      {isOpen && (
      <RecordingModal
        onClick={(e) => e.stopPropagation()}
        data-recording-modal="modal"
        style={{ 
          flexDirection: hasRecordedAudio ? 'column' : 'row',
          minWidth: hasRecordedAudio ? '380px' : '220px',
        }}
      >
        {/* 상단 영역: 웨이브폼 + 시간 + 버튼 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '16px',
          width: '100%',
        }}>
          {/* 웨이브폼 */}
          <WaveformContainer>
            {waveformBars.map((bar) => (
              <WaveformBar
                key={bar.id}
                $isActive={isRecording && !isPaused}
                $delay={bar.delay}
              />
            ))}
          </WaveformContainer>

          {/* 시간 표시 */}
          <RecordingTimer>
            {formatRecordingTime(recordingTime)}
          </RecordingTimer>

          {/* 컨트롤 버튼 */}
          <RecordingButtonGroup>
            {/* 재생/정지 버튼 - 항상 표시 */}
            {onPlayPreview && onStopPreview && (
              <IconButton
                $variant="play"
                onClick={isPlaying ? onStopPreview : onPlayPreview}
                disabled={!canPlay}
                title={isPlaying ? '재생 정지' : '미리 듣기'}
              >
                {isPlaying ? (
                  // 정지 아이콘 (재생 중일 때)
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M11.9168 6.50016C11.9168 9.05358 11.9168 10.3303 11.1233 11.1233C10.3308 11.9168 9.05358 11.9168 6.50016 11.9168C3.94675 11.9168 2.67004 11.9168 1.8765 11.1233C1.0835 10.3308 1.0835 9.05358 1.0835 6.50016C1.0835 3.94675 1.0835 2.67004 1.8765 1.8765C2.67058 1.0835 3.94675 1.0835 6.50016 1.0835C9.05358 1.0835 10.3303 1.0835 11.1233 1.8765C11.6509 2.40408 11.8275 3.14562 11.887 4.3335" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                ) : (
                  // 재생 아이콘
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M1.625 6.49984V10.2736C1.625 11.5249 2.99758 12.3152 4.11504 11.708L5.85 10.7644M1.625 4.33317V2.72605C1.625 1.4748 2.99758 0.684508 4.11504 1.29172L11.0549 5.06605C11.315 5.2044 11.5327 5.41092 11.6844 5.6635C11.8362 5.91608 11.9163 6.20518 11.9163 6.49984C11.9163 6.7945 11.8362 7.08361 11.6844 7.33618C11.5327 7.58876 11.315 7.79528 11.0549 7.93363L7.58496 9.8208" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                )}
              </IconButton>
            )}

            {/* 녹음 시작 버튼 - 녹음 중이 아닐 때만 표시 */}
            {!isActiveRecording && !hasRecordedAudio && (onOpenPermissionRequestModal || onStartRecording) && (
              <IconButton
                $variant="record"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartRecording();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                title="녹음 시작"
                data-recording-modal="button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M3.79183 1.80825C4.61486 1.33198 5.54926 1.08194 6.50016 1.0835C9.49179 1.0835 11.9168 3.50855 11.9168 6.50017C11.9168 9.4918 9.49179 11.9168 6.50016 11.9168C3.50854 11.9168 1.0835 9.4918 1.0835 6.50017C1.0835 5.5138 1.34729 4.58809 1.80825 3.79184" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </IconButton>
            )}

            {/* 녹음/일시중지 버튼 - 녹음 중일 때만 */}
            {isActiveRecording && (
              <IconButton
                $variant="record"
                onClick={isPaused ? onResumeRecording : onPauseRecording}
                title={isPaused ? '녹음 재개' : '일시 중지'}
              >
                {isPaused ? (
                  // 녹음 재개 아이콘 (빨간 원)
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M3.79183 1.80825C4.61486 1.33198 5.54926 1.08194 6.50016 1.0835C9.49179 1.0835 11.9168 3.50855 11.9168 6.50017C11.9168 9.4918 9.49179 11.9168 6.50016 11.9168C3.50854 11.9168 1.0835 9.4918 1.0835 6.50017C1.0835 5.5138 1.34729 4.58809 1.80825 3.79184" stroke="#EBEBEB" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                ) : (
                  // 일시 중지 아이콘
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M1.0835 9.75016C1.0835 10.7717 1.0835 11.282 1.40091 11.5994C1.71833 11.9168 2.22858 11.9168 3.25016 11.9168C4.27175 11.9168 4.782 11.9168 5.09941 11.5994C5.41683 11.282 5.41683 10.7717 5.41683 9.75016V3.25016C5.41683 2.22858 5.41683 1.71833 5.09941 1.40091C4.782 1.0835 4.27175 1.0835 3.25016 1.0835C2.22858 1.0835 1.71833 1.0835 1.40091 1.40091C1.0835 1.71833 1.0835 2.22858 1.0835 3.25016V7.5835M11.9168 3.25016C11.9168 2.22858 11.9168 1.71833 11.5994 1.40091C11.282 1.0835 10.7717 1.0835 9.75016 1.0835C8.72858 1.0835 8.21833 1.0835 7.90091 1.40091C7.5835 1.71833 7.5835 2.22858 7.5835 3.25016V9.75016C7.5835 10.7717 7.5835 11.282 7.90091 11.5994C8.21833 11.9168 8.72858 11.9168 9.75016 11.9168C10.7717 11.9168 11.282 11.9168 11.5994 11.5994C11.9168 11.282 11.9168 10.7717 11.9168 9.75016V5.41683" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                )}
              </IconButton>
            )}

            {/* 녹음 중지 버튼 - 녹음 중일 때만 */}
            {isActiveRecording && (
              <IconButton
                $variant="stop"
                onClick={onStopRecording}
                title="녹음 중지"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M11.9168 6.50016C11.9168 9.05358 11.9168 10.3303 11.1233 11.1233C10.3308 11.9168 9.05358 11.9168 6.50016 11.9168C3.94675 11.9168 2.67004 11.9168 1.8765 11.1233C1.0835 10.3308 1.0835 9.05358 1.0835 6.50016C1.0835 3.94675 1.0835 2.67004 1.8765 1.8765C2.67058 1.0835 3.94675 1.0835 6.50016 1.0835C9.05358 1.0835 10.3303 1.0835 11.1233 1.8765C11.6509 2.40408 11.8275 3.14562 11.887 4.3335" stroke="#6B6B6B" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </IconButton>
            )}
          </RecordingButtonGroup>

          {/* 닫기 버튼 - 항상 표시 */}
          <CloseButton onClick={handleCloseButton} title="닫기">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16" stroke="#6b6b6b" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M16 4L4 16" stroke="#6b6b6b" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </CloseButton>
        </div>

        {/* 하단 영역: 녹음 완료 후 파일명 + 버튼 */}
        {hasRecordedAudio && (
          <RecordingFooter>
            {/* 파일명 입력 - showTitleInput이 true일 때만 표시 */}
            {showTitleInput && (
              <FileNameInput
                type="text"
                value={fileName}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  // 기본 제목으로 시작하는 경우에만 업데이트
                  if (baseFileName && inputValue.startsWith(baseFileName)) {
                    setFileName(inputValue);
                  } else if (baseFileName) {
                    // 기본 제목 뒤에 사용자 입력 추가
                    const userInput = inputValue.replace(baseFileName, '');
                    setFileName(`${baseFileName}${userInput}`);
                  } else {
                    setFileName(inputValue);
                  }
                }}
                placeholder="제목"
              />
            )}
            
            {/* 삭제 / 저장 버튼 */}
            <FooterButtonGroup>
              <DeleteButton onClick={handleDelete}>
                삭제
              </DeleteButton>
              <SaveButton onClick={handleSave} disabled={isUploading}>
                {isUploading 
                  ? (showTitleInput ? '저장 중...' : '변환 중...')
                  : (showTitleInput ? '저장' : '텍스트로 변환')
                }
              </SaveButton>
            </FooterButtonGroup>
          </RecordingFooter>
        )}
      </RecordingModal>
      )}

      {/* 마이크 권한 요청 모달 (녹음 전 안내) */}
      {isPermissionRequestModalOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10004,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#2b2b2b',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              padding: '24px',
              maxWidth: '360px',
              width: '90%',
            }}
          >
            <p style={{
              color: '#ffffff',
              fontSize: '15px',
              lineHeight: 1.5,
              margin: '0 0 20px 0',
            }}>
              녹음을 시작하려면 마이크 접근 권한이 필요합니다. 브라우저에서 권한 허용을 요청합니다.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClosePermissionRequestModal?.();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  padding: '10px 16px',
                  background: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleConfirmPermissionAndStart();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  padding: '10px 16px',
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                녹음 시작
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 마이크 에러 모달 (연결 안됨, 권한 거부 등) */}
      {micErrorModal?.isOpen && micErrorModal.message && (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10004,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#2b2b2b',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              padding: '24px',
              maxWidth: '360px',
              width: '90%',
            }}
          >
            <p style={{
              color: '#ffffff',
              fontSize: '15px',
              lineHeight: 1.5,
              margin: '0 0 20px 0',
            }}>
              {micErrorModal.message}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCloseMicError?.();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                background: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#3b82f6';
              }}
            >
              확인
            </button>
          </div>
        </div>
      )}

      {/* 녹음 시작 알림 모달 */}
      {showStartNotification && (
        <div 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10004,
            background: '#2b2b2b',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            minWidth: '320px',
            pointerEvents: 'auto',
          }}
        >
          <span style={{
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 500,
            flex: 1,
          }}>
            녹음이 시작되었습니다.
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCloseNotification();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: '#6b7280',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#6b7280';
            }}
          >
            자동 닫힘..
          </button>
        </div>
      )}
    </RecordingModalOverlay>
  );
};

export default RecordingModalComponent;
